/**
 * DevopsSettingsPanel — настройки DevOps Console (P3 wave-1).
 *
 * Главное: whitelist опасных операций. По дефолту все требуют confirm=true.
 * Снимаешь чекбокс "kill_protected_process" — теперь kill systemd/sshd
 * проходит без confirm. Используй с большой осторожностью.
 */

import { useEffect, useState } from "react";
import { Save, AlertTriangle, ShieldCheck, Terminal as TerminalIcon } from "lucide-react";
import {
  fetchDevopsSettings,
  updateDevopsSettings,
  type DevopsSettings,
  type KnownDangerousOp,
} from "../../lib/api";
import { cn } from "../../lib/cn";

export function DevopsSettingsPanel() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const [settings, setSettings] = useState<DevopsSettings | null>(null);
  const [knownOps, setKnownOps] = useState<KnownDangerousOp[]>([]);

  // Локальное редактирование
  const [allowed, setAllowed] = useState<Set<string>>(new Set());
  const [retention, setRetention] = useState(30);
  const [terminalEnabled, setTerminalEnabled] = useState(true);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const r = await fetchDevopsSettings();
      setSettings(r.settings);
      setKnownOps(r.known_dangerous_ops);
      setAllowed(new Set(r.settings.allowed_dangerous_ops));
      setRetention(r.settings.audit_retention_days);
      setTerminalEnabled(r.settings.terminal_enabled);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function toggle(key: string) {
    setAllowed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function save() {
    setSaving(true);
    setErr(null);
    setOk(null);
    try {
      const r = await updateDevopsSettings({
        allowed_dangerous_ops: Array.from(allowed),
        audit_retention_days: retention,
        terminal_enabled: terminalEnabled,
      });
      setSettings(r.settings);
      setAllowed(new Set(r.settings.allowed_dangerous_ops));
      setOk("Настройки сохранены");
      window.setTimeout(() => setOk(null), 3000);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  const dirty =
    settings &&
    (JSON.stringify(Array.from(allowed).sort()) !== JSON.stringify([...settings.allowed_dangerous_ops].sort()) ||
      retention !== settings.audit_retention_days ||
      terminalEnabled !== settings.terminal_enabled);

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="flex items-center gap-2">
        <ShieldCheck className="size-5 text-accent-500" />
        <h2 className="text-h2">Настройки DevOps</h2>
      </div>

      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-small text-fg-muted flex items-start gap-2">
        <AlertTriangle className="size-4 text-amber-500 shrink-0 mt-0.5" />
        <div>
          По умолчанию все опасные операции требуют{" "}
          <code className="font-mono text-[11px] px-1 py-0.5 rounded bg-bg-subtle border border-border/40">
            confirm=true
          </code>{" "}
          в теле запроса. Если вы доверяете определённым операциям и хотите выполнять их без
          подтверждения, отметьте их ниже. Это ослабляет защиту: убедитесь, что доступ к UI
          ограничен.
        </div>
      </div>

      {err && (
        <div className="p-3 rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-500 text-small">
          {err}
        </div>
      )}
      {ok && (
        <div className="p-3 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-500 text-small">
          {ok}
        </div>
      )}

      {/* Whitelist опасных операций */}
      <section className="rounded-lg border border-border/40 bg-bg-card/30">
        <div className="px-4 py-3 border-b border-border/40">
          <h3 className="text-small font-semibold uppercase tracking-wide">
            Whitelist опасных операций
          </h3>
          <p className="text-[11px] text-fg-subtle mt-1">
            Отмеченные операции не требуют <code>confirm=true</code>.
          </p>
        </div>
        <div className="p-2">
          {knownOps.map((op) => {
            const checked = allowed.has(op.key);
            return (
              <label
                key={op.key}
                className={cn(
                  "flex items-start gap-3 p-2.5 rounded-md cursor-pointer transition-colors",
                  checked ? "bg-amber-500/10 border border-amber-500/30" : "hover:bg-bg-subtle/40 border border-transparent",
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(op.key)}
                  disabled={loading || saving}
                  className="mt-0.5 size-4 accent-amber-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-small font-medium">{op.label}</div>
                  <code className="text-[10px] font-mono text-fg-subtle">{op.key}</code>
                </div>
                {checked && <AlertTriangle className="size-4 text-amber-500 shrink-0" />}
              </label>
            );
          })}
        </div>
      </section>

      {/* Прочие настройки */}
      <section className="rounded-lg border border-border/40 bg-bg-card/30">
        <div className="px-4 py-3 border-b border-border/40">
          <h3 className="text-small font-semibold uppercase tracking-wide">Прочее</h3>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="text-small font-medium block mb-1">
              Хранить audit log (дней)
            </label>
            <input
              type="number"
              min={1}
              max={365}
              value={retention}
              onChange={(e) => setRetention(parseInt(e.target.value || "30", 10))}
              className="w-32 px-2 py-1.5 rounded-md bg-bg-subtle border border-border/40 text-small focus:outline-none focus:ring-1 focus:ring-accent-500"
            />
            <p className="text-[11px] text-fg-subtle mt-1">
              Записи старше этого срока могут быть удалены при следующей очистке.
            </p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={terminalEnabled}
              onChange={(e) => setTerminalEnabled(e.target.checked)}
              className="mt-0.5 size-4 accent-accent-500"
            />
            <div>
              <div className="text-small font-medium flex items-center gap-1.5">
                <TerminalIcon className="size-3.5" />
                Live-терминал включён
              </div>
              <p className="text-[11px] text-fg-subtle mt-0.5">
                Открывает root-bash на хосте через WebSocket. Закрытие отключает вкладку
                «Терминал» в DevOps Console.
              </p>
            </div>
          </label>
        </div>
      </section>

      <div className="flex items-center gap-2 sticky bottom-0 py-3 bg-gradient-to-t from-bg via-bg/90 to-transparent">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving || loading}
          className={cn(
            "inline-flex items-center gap-2 px-4 py-2 rounded-md text-small font-medium transition-colors",
            dirty
              ? "bg-accent-500 text-white hover:bg-accent-500/90"
              : "bg-bg-subtle text-fg-subtle cursor-not-allowed",
          )}
        >
          <Save className="size-4" />
          {saving ? "Сохраняю…" : "Сохранить"}
        </button>
        {dirty && <span className="text-small text-amber-500">Есть несохранённые изменения</span>}
      </div>
    </div>
  );
}
